"use client"

import type { FC, SVGProps } from "react"
import * as React from "react"
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu"
import {
	IconCheckOutline18 as Check,
	IconChevronRightOutline18 as ChevronRight,
	IconCircleDashedOutline18 as Circle,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/browser"

import type { IconType } from "../../icons"
import { Loader } from "../loader"

const DropdownMenuTrigger = React.memo(DropdownMenuPrimitive.Trigger)

export const DropdownMenuGroup = React.memo(DropdownMenuPrimitive.Group)

export const DropdownMenuPortal = React.memo(DropdownMenuPrimitive.Portal)

export const DropdownMenuSub = React.memo(DropdownMenuPrimitive.Sub)

export const DropdownMenuRadioGroup = React.memo(
	DropdownMenuPrimitive.RadioGroup,
)

interface DropdownMenuProps extends DropdownMenuPrimitive.DropdownMenuProps {
	trigger: React.ReactNode
	contentProps?: DropdownMenuPrimitive.DropdownMenuContentProps
	fitTriggerWidth?: boolean
	containerRef?: React.RefObject<HTMLDivElement | null>
	className?: string
}

function DropdownMenuInner({
	trigger,
	children,
	fitTriggerWidth = false,
	contentProps = {},
	open,
	onOpenChange,
	defaultOpen,
	containerRef,
	className,
	modal = true,
}: DropdownMenuProps) {
	const [isOpen, setIsOpen] = React.useState(defaultOpen ?? false)
	return (
		<DropdownMenuPrimitive.Root
			open={open ?? isOpen}
			onOpenChange={(value) => {
				setIsOpen(value)
				onOpenChange?.(value)
			}}
			modal={modal}
		>
			<DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
			<DropdownMenuContent
				sideOffset={4}
				containerRef={containerRef}
				{...contentProps}
				onCloseAutoFocus={(e) => {
					e.preventDefault()
				}}
				style={{
					width: fitTriggerWidth
						? "var(--radix-popper-anchor-width)"
						: undefined,
				}}
				className={className}
			>
				{children}
			</DropdownMenuContent>
		</DropdownMenuPrimitive.Root>
	)
}

export const DropdownMenu = React.memo(DropdownMenuInner)

export const DropdownMenuSubTrigger = React.memo(
	React.forwardRef<
		React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
		React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
			inset?: boolean
			Icon?: LucideIcon | IconType
			shortcut?: React.ReactNode
		}
	>(({ className, inset, Icon, shortcut, children, ...props }, ref) => (
		<DropdownMenuPrimitive.SubTrigger
			ref={ref}
			className={cn(
				"flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm font-medium outline-none focus:bg-gray-100 data-[state=open]:bg-gray-100 dark:focus:bg-gray-800 dark:data-[state=open]:bg-gray-800",
				inset && "pl-8",
				className,
			)}
			{...props}
		>
			{Icon ? (
				<Icon
					strokeWidth={1.75}
					className="mr-2 h-4 w-4 flex-none text-secondary"
				/>
			) : null}
			{children}
			{shortcut ? (
				<DropdownMenuShortcut>{shortcut}</DropdownMenuShortcut>
			) : null}
			<ChevronRight
				className={cn("h-3 w-3 flex-none", shortcut ? "ml-2" : "ml-auto")}
			/>
		</DropdownMenuPrimitive.SubTrigger>
	)),
)

export const DropdownMenuSubContent = React.memo(
	React.forwardRef<
		React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
		React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
	>(({ className, ...props }, ref) => (
		<DropdownMenuPrimitive.SubContent
			ref={ref}
			className={cn(
				"z-50 min-w-[8rem] overflow-hidden rounded-lg bg-white p-1 text-primary shadow-lg ring-1 ring-black/15 !duration-150 ease-out-cubic data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=open]:fade-in data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 dark:bg-gray-850 dark:ring-white/15",
				className,
			)}
			{...props}
		/>
	)),
)

const DropdownMenuContent = React.forwardRef<
	React.ElementRef<typeof DropdownMenuPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content> & {
		containerRef?: React.RefObject<HTMLDivElement | null>
	}
>(({ containerRef, className, sideOffset = 4, ...props }, ref) => (
	<DropdownMenuPrimitive.Portal container={containerRef?.current}>
		<DropdownMenuPrimitive.Content
			ref={ref}
			sideOffset={sideOffset}
			className={cn(
				"z-50 w-60 min-w-[8rem] max-w-[100vw] overflow-hidden rounded-lg bg-white p-1 text-primary shadow-lg ring-1 ring-black/15 dark:bg-gray-850 dark:shadow-black dark:ring-white/15",
				"!duration-150 ease-out-cubic data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=open]:fade-in data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
				className,
			)}
			{...props}
		/>
	</DropdownMenuPrimitive.Portal>
))

export const DropdownMenuItem = React.memo(
	React.forwardRef<
		React.ElementRef<typeof DropdownMenuPrimitive.Item>,
		React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
			inset?: boolean
			Icon?: LucideIcon | IconType
			/** Trails the label, for a mark that reads as state rather than as a topic. */
			IconRight?: LucideIcon | IconType
			iconClassName?: string
			iconStrokeWidth?: number
			shortcut?: React.ReactNode
			isLoading?: boolean
		}
	>(
		(
			{
				children,
				shortcut,
				className,
				isLoading,
				Icon,
				IconRight,
				iconClassName,
				iconStrokeWidth = 1.75,
				inset,
				...props
			},
			ref,
		) => (
			<DropdownMenuPrimitive.Item
				ref={ref}
				className={cn(
					"relative flex cursor-default select-none items-center rounded-md p-1 px-2 py-1.5 text-sm font-medium outline-none focus:bg-gray-100 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 dark:focus:bg-gray-800",
					inset && "pl-8",
					className,
				)}
				{...props}
			>
				{isLoading && (
					<div className="absolute inset-0 flex items-center justify-center bg-white dark:bg-gray-900">
						<Loader />
					</div>
				)}
				{Icon ? (
					<Icon
						strokeWidth={iconStrokeWidth}
						className={cn(
							"mr-2 h-4 w-4 flex-none text-secondary",
							iconClassName,
						)}
					/>
				) : null}
				{children}
				{IconRight ? (
					<IconRight
						strokeWidth={iconStrokeWidth}
						className={cn(
							"ml-auto h-4 w-4 flex-none text-secondary",
							iconClassName,
						)}
					/>
				) : null}
				{shortcut ? (
					<DropdownMenuShortcut>{shortcut}</DropdownMenuShortcut>
				) : null}
			</DropdownMenuPrimitive.Item>
		),
	),
)

export const DropdownMenuCheckboxItem = React.memo(
	React.forwardRef<
		React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
		React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>
	>(({ className, children, checked, ...props }, ref) => (
		<DropdownMenuPrimitive.CheckboxItem
			ref={ref}
			className={cn(
				"relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm font-medium outline-none focus:bg-gray-100 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 dark:focus:bg-gray-800",
				className,
			)}
			checked={checked}
			{...props}
		>
			{children}
			<span className="absolute right-2 flex h-3 w-3 items-center justify-center">
				<DropdownMenuPrimitive.ItemIndicator>
					<Check className="h-4 w-4" />
				</DropdownMenuPrimitive.ItemIndicator>
			</span>
		</DropdownMenuPrimitive.CheckboxItem>
	)),
)

export const DropdownMenuRadioItem = React.memo(
	React.forwardRef<
		React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
		React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>
	>(({ className, children, ...props }, ref) => (
		<DropdownMenuPrimitive.RadioItem
			ref={ref}
			className={cn(
				"relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm font-medium outline-none focus:bg-gray-100 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 dark:focus:bg-gray-800",
				className,
			)}
			{...props}
		>
			<span className="absolute left-2 flex h-3 w-3 items-center justify-center">
				<DropdownMenuPrimitive.ItemIndicator>
					<Circle className="h-2 w-2 fill-current" />
				</DropdownMenuPrimitive.ItemIndicator>
			</span>
			{children}
		</DropdownMenuPrimitive.RadioItem>
	)),
)

export const DropdownMenuLabel = React.memo(
	React.forwardRef<
		React.ElementRef<typeof DropdownMenuPrimitive.Label>,
		React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
			inset?: boolean
		}
	>(({ className, inset, ...props }, ref) => (
		<DropdownMenuPrimitive.Label
			ref={ref}
			className={cn(
				"px-2 py-1.5 text-sm font-medium text-secondary",
				inset && "pl-8",
				className,
			)}
			{...props}
		/>
	)),
)

export const DropdownMenuSeparator = React.memo(
	React.forwardRef<
		React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
		React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
	>(({ className, ...props }, ref) => (
		<DropdownMenuPrimitive.Separator
			ref={ref}
			className={cn("-mx-1 my-1 h-px bg-gray-100 dark:bg-gray-800", className)}
			{...props}
		/>
	)),
)

const DropdownMenuShortcut = React.memo(
	({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => {
		return (
			<span
				className={cn(
					"ml-auto text-xs tracking-widest text-gray-500",
					className,
				)}
				{...props}
			/>
		)
	},
)

type LucideIcon = FC<SVGProps<SVGSVGElement>>
